import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatDate, type ExportStatus, type Me, type Summary } from '../api.ts';
import { Icon } from '../components/Icon.tsx';
import { CoordPicker } from '../components/CoordPicker.tsx';

/**
 * Настройки (ТЗ 4.1): кто вошёл, формат инвентарного номера, деньги,
 * переходы к разделам, выход.
 */
export function SettingsScreen({
  me,
  onSignedOut,
  onProfileChanged,
}: {
  me: Me;
  onSignedOut: () => void;
  onProfileChanged: () => void;
}) {
  const navigate = useNavigate();
  const [prefix, setPrefix] = useState(me.inv_prefix);
  const [padding, setPadding] = useState(String(me.inv_padding));
  const [exportStatus, setExportStatus] = useState<ExportStatus | null>(null);
  const [summary, setSummary] = useState<Summary | null>(null);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  const [home, setHome] = useState({
    latitude: me.home_latitude ? String(Number(me.home_latitude)) : '',
    longitude: me.home_longitude ? String(Number(me.home_longitude)) : '',
    precisionM: String(me.home_radius_m ?? 5000),
  });

  useEffect(() => {
    void api.exportStatus().then(setExportStatus);
    void api.summary().then(setSummary);
  }, []);

  const changed = prefix !== me.inv_prefix || Number(padding) !== me.inv_padding;
  const issued = summary ? summary.specimens > 0 : false;

  async function saveNumbering() {
    setError(null);
    try {
      await api.updateProfile({ inv_prefix: prefix, inv_padding: Number(padding) });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
  }

  /**
   * Режим «без цен» (ТЗ D4). Сохраняется сразу, без кнопки: переключатель
   * с отложенным «сохранить» — это способ решить, что деньги спрятаны,
   * когда они ещё видны.
   */
  async function toggleHidePrices(next: boolean) {
    setError(null);
    try {
      await api.updateProfile({ hide_prices: next });
      onProfileChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
  }

  /**
   * Отправка снимков наружу. Выключатель здесь нужен не меньше, чем сам
   * выключатель «без цен»: всё остальное приложение живёт на своём сервере
   * и ничего никуда не отправляет, а это — единственное исключение.
   */
  async function toggleAi(next: boolean) {
    setError(null);
    try {
      await api.updateProfile({ ai_enabled: next });
      onProfileChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
  }

  async function saveCurrency(code: string) {
    setError(null);
    try {
      await api.updateProfile({ default_currency: code });
      onProfileChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
  }

  /**
   * Домашняя зона (ТЗ 7.3, правило 2). Координаты внутри круга не
   * предлагаются, не попадают на карту и не уходят в выгрузку: снимки
   * делаются дома, и без этого правила приложение однажды уговорит
   * поставить точку находки по адресу квартиры.
   */
  async function saveHome() {
    setError(null);
    try {
      const lat = home.latitude.trim() ? Number(home.latitude.replace(',', '.')) : null;
      const lng = home.longitude.trim() ? Number(home.longitude.replace(',', '.')) : null;
      await api.updateProfile({
        home_latitude: lat,
        home_longitude: lng,
        home_radius_m: Number(home.precisionM) || 5000,
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      onProfileChanged();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    }
  }

  async function signOut() {
    await api.logout();
    onSignedOut();
  }

  // Показываем СЛЕДУЮЩИЙ номер, а не первый: счётчик уже не на нуле,
  // и «MIN-00001» в подсказке вводил бы в заблуждение.
  const next = me.inv_counter + 1;
  const example = `${prefix}${String(next).padStart(Number(padding) || 1, '0')}`;

  return (
    <div className="app">
      <div className="topbar topbar--bordered">
        <button className="icon-btn" onClick={() => navigate(-1)} aria-label="Назад">
          <Icon name="back" className="icon muted" />
        </button>
        <div className="spacer" />
        <h2 className="topbar-title">Настройки</h2>
        <div className="spacer" />
        <span style={{ width: 20 }} />
      </div>

      <div className="scroll">
        <div className="label-key" style={{ marginTop: 'var(--space-4)' }}>
          Вы вошли как
        </div>
        <div className="account">
          <div>
            <div className="account-name">{me.display_name ?? me.email}</div>
            <div className="hint">{me.email}</div>
          </div>
        </div>

        <div className="label-key">Инвентарный номер</div>
        <div className="size-row" style={{ gridTemplateColumns: '2fr 1fr' }}>
          <input
            className="field-input"
            value={prefix}
            maxLength={20}
            aria-label="Префикс"
            onChange={(e) => setPrefix(e.target.value)}
          />
          <input
            className="field-input"
            inputMode="numeric"
            value={padding}
            aria-label="Разрядность"
            onChange={(e) => setPadding(e.target.value.replace(/\D/g, '').slice(0, 2))}
          />
        </div>
        <div className="hint">Следующий будет выглядеть так: {example}</div>

        {issued && (
          <div className="notice notice--warn" style={{ marginTop: 'var(--space-3)' }}>
            <Icon name="alert" className="icon icon-sm" />
            <span>
              Номера уже выданы и, возможно, нанесены на образцы. Смена формата
              подействует только на новые — старые останутся как есть.
            </span>
          </div>
        )}

        {changed && (
          <button
            className="btn btn--full"
            style={{ marginTop: 'var(--space-3)' }}
            onClick={saveNumbering}
          >
            Сохранить формат
          </button>
        )}
        {saved && <div className="hint centered">Сохранено</div>}
        {error && (
          <div className="notice notice--danger" style={{ marginTop: 'var(--space-3)' }}>
            {error}
          </div>
        )}

        <div className="label-key">Деньги</div>
        <button
          type="button"
          className="switch-row"
          role="switch"
          aria-checked={me.hide_prices}
          onClick={() => void toggleHidePrices(!me.hide_prices)}
        >
          <div>
            Режим «без цен»
            <span className="hint">
              {me.hide_prices
                ? 'суммы скрыты везде, включая выгрузку; данные целы'
                : 'суммы видны в карточке, статистике и выгрузке'}
            </span>
          </div>
          <div className="spacer" />
          <span className={me.hide_prices ? 'switch is-on' : 'switch'} aria-hidden="true">
            <i />
          </span>
        </button>

        <div className="label-key">Определение по фотографиям</div>
        <button
          type="button"
          className="switch-row"
          role="switch"
          aria-checked={me.ai_enabled}
          onClick={() => void toggleAi(!me.ai_enabled)}
        >
          <div>
            Спрашивать модель о минерале
            <span className="hint">
              {me.ai_enabled
                ? 'до трёх снимков, размеры и место уходят наружу по кнопке в карточке; цена и координаты не уходят'
                : 'снимки никуда не отправляются, кнопка в карточке не работает'}
            </span>
          </div>
          <div className="spacer" />
          <span className={me.ai_enabled ? 'switch is-on' : 'switch'} aria-hidden="true">
            <i />
          </span>
        </button>

        {!me.hide_prices && (
          <>
            <div className="label-key">Валюта по умолчанию</div>
            <div className="segment">
              {['RUB', 'USD', 'EUR'].map((code) => (
                <button
                  key={code}
                  className={me.default_currency === code ? 'is-selected' : ''}
                  onClick={() => void saveCurrency(code)}
                >
                  {code}
                </button>
              ))}
            </div>
            <div className="hint">Подставляется в новые цены и оценки; у каждой записи своя</div>
          </>
        )}

        <div className="label-key">Домашняя зона</div>
        <p className="hint" style={{ marginTop: 0 }}>
          Координаты снимков, сделанных в этом круге, приложение молча игнорирует:
          не предлагает их, не рисует на карте и не пишет в выгрузку. Большинство
          образцов фотографируют дома.
        </p>
        <CoordPicker
          label="Точка дома"
          latitude={home.latitude}
          longitude={home.longitude}
          precisionM={home.precisionM}
          radiusM={Number(home.precisionM) || 5000}
          onChange={setHome}
        />
        <div className="label-key">Радиус зоны</div>
        <div className="segment">
          {[1000, 5000, 20000].map((radius) => (
            <button
              key={radius}
              className={Number(home.precisionM) === radius ? 'is-selected' : ''}
              onClick={() => setHome((h) => ({ ...h, precisionM: String(radius) }))}
            >
              {radius >= 1000 ? `${radius / 1000} км` : `${radius} м`}
            </button>
          ))}
        </div>
        <button className="btn btn--full" style={{ marginTop: 'var(--space-3)' }} onClick={saveHome}>
          Сохранить домашнюю зону
        </button>

        <div className="label-key">Разделы</div>
        <Link className="menu-row" to="/map">
          <Icon name="pin" className="icon icon-sm muted" />
          <div>
            Карта находок
            <span className="hint">точки выездов и образцов, кроме домашней зоны</span>
          </div>
          <div className="spacer" />
          <Icon name="chevron" className="icon icon-sm muted" />
        </Link>
        <Link className="menu-row" to="/stats">
          <Icon name="chart" className="icon icon-sm muted" />
          <div>
            Статистика
            <span className="hint">
              {summary
                ? `${summary.specimens} образцов, ${summary.species} видов`
                : 'что есть и чего не хватает'}
            </span>
          </div>
          <div className="spacer" />
          <Icon name="chevron" className="icon icon-sm muted" />
        </Link>
        <Link className="menu-row" to="/reference">
          <Icon name="book" className="icon icon-sm muted" />
          <div>
            Справочник минералов
            <span className="hint">виды и разновидности из Mindat, привязка своих названий</span>
          </div>
          <div className="spacer" />
          <Icon name="chevron" className="icon icon-sm muted" />
        </Link>
        <Link className="menu-row" to="/labels">
          <Icon name="printer" className="icon icon-sm muted" />
          <div>
            Печать этикеток
            <span className="hint">QR и номер на лист самоклейки A4</span>
          </div>
          <div className="spacer" />
          <Icon name="chevron" className="icon icon-sm muted" />
        </Link>
        <Link className="menu-row" to="/export">
          <Icon name="download" className="icon icon-sm muted" />
          <div>
            Выгрузка коллекции
            <span className="hint">
              {exportStatus?.last_export_at
                ? `последняя — ${formatDate(exportStatus.last_export_at.slice(0, 10))}`
                : 'ещё ни разу не делалась'}
            </span>
          </div>
          <div className="spacer" />
          <Icon name="chevron" className="icon icon-sm muted" />
        </Link>
        <Link className="menu-row" to="/trips/new">
          <Icon name="route" className="icon icon-sm muted" />
          <div>
            Новый выезд
            <span className="hint">место и координаты сразу для всей партии</span>
          </div>
          <div className="spacer" />
          <Icon name="chevron" className="icon icon-sm muted" />
        </Link>

        <div className="label-key">Выход</div>
        {confirmSignOut ? (
          <div className="actions" style={{ marginTop: 0 }}>
            <button className="btn" onClick={() => setConfirmSignOut(false)}>
              Остаться
            </button>
            <button className="btn btn--danger" onClick={signOut}>
              Выйти
            </button>
          </div>
        ) : (
          <button className="btn btn--full" onClick={() => setConfirmSignOut(true)}>
            Выйти из аккаунта
          </button>
        )}
      </div>
    </div>
  );
}
